import axios from 'axios';
import Cookies from 'js-cookie';
import { useState, useEffect } from 'react';
import Navbar from "../../constants/Navbar";
import { useNavigate } from "react-router";
import { useAlert } from "../../constants/AlertProvider";
import { getUserDetails } from "../../utils/getUserDetails"

export default function RecPreferences() {
    const navigate = useNavigate();
    const showAlert = useAlert();
    const [companyId, setCompanyId] = useState(null);
    const [companyData, setCompanyData] = useState({
        hiring_goals: "",
        roles_hiring_for: "",
        hiring_timeline: "",
        work_setting: "",
        location: "",
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const userDetails = await getUserDetails();
                const fetchedCompanyId = userDetails.company;
                if (fetchedCompanyId) {
                    setCompanyId(fetchedCompanyId);
                    const companyRes = await axios.get(`/api/company/${fetchedCompanyId}/`, { withCredentials: true });
                    setCompanyData(prev => ({ ...prev, ...companyRes.data }));
                } else {
                    showAlert("No company associated with this user.", "error");
                }
            } catch (error) {
                showAlert("Something went wrong while fetching data!", "error");
                console.error("error", error);
            }
        }

        fetchData();
    }, [showAlert]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCompanyData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = async (e) => {
        e.preventDefault();
        if (!companyId) {
            showAlert("Company not found. Cannot save preferences.", "error");
            return;
        }
        try {
            // Only hiring_goals and roles_hiring_for exist on the Company
            // model -- hiring_timeline/work_setting/location are collected
            // here as local match filters but have nowhere to persist yet.
            const { hiring_goals, roles_hiring_for } = companyData;
            await axios.patch(`/api/company/${companyId}/`, { hiring_goals, roles_hiring_for }, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': Cookies.get('csrftoken'),
                },
                withCredentials: true,
            });
            showAlert('Preferences saved!', "success");
            navigate('/orghome');
        } catch (error) {
            showAlert('Something went wrong!', "error");
            console.error("error", error);
        }
    }

    return (
        <div className="min-h-screen bg-white dark:bg-gray-900">
            {/* Navbar */}
            <Navbar />

            {/* Main Container */}
            <form onSubmit={handleSave} className="flex flex-col px-4 sm:px-6 lg:px-8 py-10">
                <main className="w-full max-w-7xl mx-auto bg-white dark:bg-gray-900">
                    {/* Heading */}
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-gray-100 mb-10 text-center lg:text-left">
                        Help us find your match!
                    </h1>
                    <div className="flex flex-col gap-y-12">
                        <div className="grid grid-cols-1 lg:grid-cols-3 gap-x-8 gap-y-6">
                            <div className="lg:col-span-1 flex flex-col gap-y-2">
                                <h3 className="text-base text-gray-900 dark:text-gray-100 font-semibold">Preferences</h3>
                                <p className="text-sm text-gray-600 dark:text-gray-400 font-normal">This will be used to match you with the right talent</p>
                            </div>
                            <div className="lg:col-span-2 flex flex-col">
                                <div className="flex flex-col gap-y-2 mb-8">
                                    <label htmlFor="hiring-goals" className="text-sm text-gray-900 dark:text-gray-100 font-medium">Hiring Goals</label>
                                    <select id="hiring-goals" name="hiring_goals" value={companyData.hiring_goals || ''} onChange={handleChange} className="w-full md:w-4/5 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 rounded-lg text-sm text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:border-blue-500">
                                        <option value="Find full-time employees" className="text-gray-700">Find full-time employees</option>
                                        <option value="Source contract" className="text-gray-700">Source contract or freelance talent</option>
                                        <option value="talent pipeline" className="text-gray-700">Build a talent pipeline</option>
                                        <option value="Collaborate" className="text-gray-700">Collaborate with students or volunteers</option>
                                    </select>
                                </div>
                                <div className="w-full md:w-4/5 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 mb-8">
                                    <div className="flex flex-col gap-y-2">
                                        <label htmlFor="role-type" className="text-sm text-gray-900 dark:text-gray-100 font-medium">What type of roles you are looking to fill?</label>
                                        <select id="role-type" name="roles_hiring_for" value={companyData.roles_hiring_for || ""} onChange={handleChange} className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 rounded-lg text-sm text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:border-blue-500">
                                            <option value="All" className="text-gray-700">All</option>
                                            <option value="Full time" className="text-gray-700">Full time</option>
                                            <option value="Part time" className="text-gray-700">Part time</option>
                                            <option value="Volunteer" className="text-gray-700">Volunteer</option>
                                            <option value="AI gig" className="text-gray-700">AI gig</option>
                                        </select>
                                    </div>
                                    <div className="flex flex-col gap-y-2">
                                        <label htmlFor="how-soon" className="text-sm text-gray-900 dark:text-gray-100 font-medium">How soon do you need someone</label>
                                        <select id="how-soon" name="hiring_timeline" value={companyData.hiring_timeline || ''} onChange={handleChange} className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 rounded-lg text-sm text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:border-blue-500">
                                            <option value="ASAP" className="text-gray-700">ASAP</option>
                                            <option value="1-3 months" className="text-gray-700">1-3 months</option>
                                            <option value="3-6 months" className="text-gray-700">3-6 months</option>
                                            <option value="Flexible" className="text-gray-700">Flexible</option>
                                        </select>
                                    </div>
                                </div>

                                {/* What roles */}
                                <div className="w-full md:w-4/5 flex flex-col gap-y-2 mb-8">
                                    <label htmlFor="roles" className="text-sm text-gray-900 dark:text-gray-100 font-medium">What positions would you want to hire?</label>
                                    <input id="roles" name="roles_hiring_for" value={companyData.roles_hiring_for || ''} onChange={handleChange} className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 rounded-lg text-sm text-gray-900 dark:text-gray-100 font-medium focus:outline-none focus:border-blue-500" />
                                </div>

                                <div className="w-full md:w-2/3 flex flex-col gap-y-2 mb-8">
                                    <label htmlFor="work-setting" className="text-sm text-gray-900 dark:text-gray-100 font-medium">Where will the talent you hire be based?</label>
                                    <select id="work-setting" name="work_setting" value={companyData.work_setting || ''} onChange={handleChange} className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 rounded-lg text-sm text-gray-900 dark:text-gray-100 font-normal focus:outline-none focus:border-blue-500">
                                        <option value="Remote" className="text-gray-700">Remote</option>
                                        <option value="On-site" className="text-gray-700">On-site</option>
                                        <option value="Hybrid" className="text-gray-700">Hybrid</option>
                                    </select>
                                </div>

                                <div className="w-full md:w-2/3 flex flex-col gap-y-2 mb-8">
                                    <label htmlFor="location" className="text-sm text-gray-900 dark:text-gray-100 font-medium">Location</label>
                                    <input id="location" name="location" value={companyData.location || ''} onChange={handleChange} className="border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 p-2 rounded-lg text-sm text-gray-900 dark:text-gray-100 font-medium focus:outline-none focus:border-blue-500" />
                                </div>
                            </div>
                        </div>
                    </div>
                </main>
                <div className="w-full flex justify-end p-6">
                    <button type="submit" className="text-white h-10 text-center font-medium text-sm bg-blue-700 px-4 rounded-lg cursor-pointer hover:bg-blue-600">
                        Start Matching!
                    </button>
                </div>
            </form>
        </div>
    );
}